// SUB-OFFICE 7 — telemetry. The surveillance conceit only works if the Bureau
// is never wrong: fine-grained composition metrics (backspaces, >2s pauses,
// paste) are asserted ONLY when no IME composition event occurred and the
// pointer is fine. Any compositionstart (or coarse pointer) downgrades the
// captured set to metrics that survive autocorrect: elapsed time and blurs.
// Movement during adjudication is measured in meters and shipped with
// /api/ack — the server re-derives the observation from numeric bands.

import type { Telemetry } from '../shared/types';
import type { Player } from './entities/player';

const PAUSE_S = 2;

export class CompositionMeter {
  private t0 = 0; // first keystroke, ms
  private lastKey = 0;
  private backspaces = 0;
  private pauses2s = 0;
  private blurs = 0;
  private pasted = false;
  private ime: boolean;
  private el: HTMLTextAreaElement | null = null;
  private onKey = (e: KeyboardEvent): void => this.key(e);
  private onPaste = (): void => {
    this.pasted = true;
  };
  private onComp = (): void => {
    this.ime = true;
  };
  private onBlur = (): void => {
    this.blurs++;
  };

  constructor() {
    // coarse pointers imply soft keyboards imply autocorrect: robust set only
    this.ime = window.matchMedia('(pointer: coarse)').matches;
  }

  attach(el: HTMLTextAreaElement): void {
    this.el = el;
    el.addEventListener('keydown', this.onKey);
    el.addEventListener('paste', this.onPaste);
    el.addEventListener('compositionstart', this.onComp);
    el.addEventListener('blur', this.onBlur);
  }

  detach(): void {
    if (!this.el) return;
    this.el.removeEventListener('keydown', this.onKey);
    this.el.removeEventListener('paste', this.onPaste);
    this.el.removeEventListener('compositionstart', this.onComp);
    this.el.removeEventListener('blur', this.onBlur);
    this.el = null;
  }

  private key(e: KeyboardEvent): void {
    const now = performance.now();
    if (this.t0 === 0) this.t0 = now;
    if (this.lastKey > 0 && now - this.lastKey > PAUSE_S * 1000) this.pauses2s++;
    this.lastKey = now;
    if (e.key === 'Backspace' || e.key === 'Delete') this.backspaces++;
  }

  /** Seconds since first keystroke (0 if nothing was typed). */
  get seconds(): number {
    return this.t0 === 0 ? 0 : Math.round((performance.now() - this.t0) / 1000);
  }

  get imeDowngraded(): boolean {
    return this.ime;
  }

  /** The contract telemetry object — truth-gated. */
  collect(): Telemetry {
    const t: Telemetry = { seconds: this.seconds, blurs: this.blurs };
    if (!this.ime) {
      t.backspaces = this.backspaces;
      t.pauses2s = this.pauses2s;
      t.pasted = this.pasted;
    }
    return t;
  }
}

// ---------------------------------------------------------------------------
// Movement during adjudication: meters walked while the offering is considered
// ---------------------------------------------------------------------------

export class MovementMeter {
  private lastX = 0;
  private lastZ = 0;
  private total = 0;
  private running = false;

  constructor(private player: Player) {}

  start(): void {
    this.lastX = this.player.pos.x;
    this.lastZ = this.player.pos.z;
    this.total = 0;
    this.running = true;
  }

  update(): void {
    if (!this.running) return;
    const dx = this.player.pos.x - this.lastX;
    const dz = this.player.pos.z - this.lastZ;
    this.total += Math.hypot(dx, dz);
    this.lastX = this.player.pos.x;
    this.lastZ = this.player.pos.z;
  }

  stop(): { movedM: number; stoodStill: boolean } {
    this.running = false;
    const m = Math.round(this.total * 10) / 10;
    return { movedM: m, stoodStill: m < 0.5 };
  }
}
